import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import styled from 'styled-components';

const AlbumContainer = styled(motion.section)`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  box-shadow: 0 15px 40px rgba(255, 105, 180, 0.3);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: linear-gradient(90deg, #ff1493, #ff69b4, #da70d6);
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 150px;
    height: 150px;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 150 150"><path d="M0,150 L150,150 L150,0 C150,82.8 82.8,150 0,150 Z" fill="%23ffb6c1" opacity="0.2" /></svg>');
    pointer-events: none;
  }
`;

const AlbumTitle = styled(motion.h3)`
  font-size: 2.2rem;
  color: #d84079;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  font-weight: 700;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, #ff1493, transparent);
    border-radius: 2px;
  }
`;

const AlbumDescription = styled(motion.p)`
  font-size: 1.3rem;
  line-height: 1.9;
  margin-bottom: 1.5rem;
  text-align: right;
  color: #444;
  max-width: 95%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 500;
  position: relative;
  
  &:before, &:after {
    content: '"';
    font-size: 2.5rem;
    color: rgba(216, 64, 121, 0.2);
    position: absolute;
  }
  
  &:before {
    top: -15px;
    right: -20px;
  }
  
  &:after {
    bottom: -25px;
    left: -20px;
  }
`;

const BookContainer = styled(motion.div)`
  width: 100%;
  max-width: 650px;
  height: 450px;
  margin: 3rem auto;
  position: relative;
  perspective: 2000px;
  
  @media (max-width: 768px) {
    height: 400px;
  }
  
  @media (max-width: 480px) {
    height: 350px;
  }
`;

const Book = styled(motion.div)`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transform: rotateX(10deg);
  transition: transform 0.5s ease;
`;

const BookCover = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: #f8bbd0;
  background-image: radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.2) 5%, transparent 5%),
                    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.2) 5%, transparent 5%),
                    radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.25) 8%, transparent 8%),
                    linear-gradient(45deg, rgba(0, 0, 0, 0.05) 25%, transparent 25%),
                    linear-gradient(-45deg, rgba(0, 0, 0, 0.05) 25%, transparent 25%);
  background-size: 200px 200px, 200px 200px, 100px 100px, 20px 20px, 20px 20px;
  border-radius: 15px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  backface-visibility: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2.5rem;
  transform: ${props => props.isOpen ? 'rotateY(-180deg)' : 'rotateY(0)'};
  transition: transform 1.2s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

const FrontCover = styled(BookCover)`
  z-index: 2;
  background-color: #f8bbd0;
  border: 8px solid #fff;
  
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
    width: 20px;
    height: 70%;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 90%;
    height: 90%;
    top: 5%;
    left: 5%;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 10px;
    pointer-events: none;
  }
`;

const BackCover = styled(BookCover)`
  transform: rotateY(180deg);
  background-color: #f8bbd0;
  border: 8px solid #fff;
  
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 20px;
    transform: translateY(-50%);
    width: 20px;
    height: 70%;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }
`;

const Page = styled(motion.div)`
  position: absolute;
  width: 95%;
  height: 95%;
  top: 2.5%;
  left: 2.5%;
  background: #fff;
  border-radius: 10px;
  backface-visibility: hidden;
  background-image: linear-gradient(to right, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  transform-origin: left center;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: transform 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, rgba(0, 0, 0, 0.05) 0%, transparent 5%);
    border-radius: 10px;
    pointer-events: none;
  }
`;

const PageBack = styled(Page)`
  transform: rotateY(180deg);
`;

const CoverTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
  margin-bottom: 1.5rem;
  font-weight: 800;
  letter-spacing: 1px;
`;

const CoverSubtitle = styled.p`
  font-size: 1.4rem;
  color: white;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.4);
  max-width: 80%;
  line-height: 1.6;
`;

const FlowerDecoration = styled.div`
  position: absolute;
  top: ${props => props.top || '10px'};
  left: ${props => props.left || '10px'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  width: ${props => props.size || '40px'};
  height: ${props => props.size || '40px'};
  color: ${props => props.color || '#ff6b81'};
  font-size: ${props => props.fontSize || '2rem'};
  transform: rotate(${props => props.rotate || '0deg'});
  opacity: 0.85;
  z-index: 5;
  filter: drop-shadow(2px 2px 3px rgba(0, 0, 0, 0.2));
`;

const EmptyPhotoFrame = styled(motion.div)`
  width: 200px;
  height: 200px;
  background: #f8f8f8;
  border: 12px solid #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  margin: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${props => props.rotate || '0deg'});
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0) 100%);
    z-index: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.1);
    z-index: 2;
    pointer-events: none;
  }
`;

const FrameContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 3;
`;

const FrameIcon = styled.div`
  font-size: 3.5rem;
  color: rgba(216, 64, 121, 0.2);
  margin-bottom: 10px;
`;

const FrameText = styled.div`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  font-style: italic;
`;

const PageContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 15px;
  right: ${props => props.right || 'auto'};
  left: ${props => props.left || 'auto'};
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.4);
  font-family: 'Times New Roman', serif;
  font-style: italic;
`;

const PageCaption = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: #444;
  margin-top: 0.8rem;
  font-style: italic;
  width: 100%;
  direction: rtl;
  pointer-events: none;
  position: relative;
  z-index: 5;
  line-height: 1.5;
  font-weight: 500;
`;

const TextContainer = styled.div`
  transform: ${props => props.isReversed ? 'scaleX(-1)' : 'none'};
  width: 100%;
  display: flex;
  justify-content: center;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  gap: 3rem;
`;

const NavButton = styled(motion.button)`
  background: linear-gradient(45deg, ${props => props.bgStart || '#ff1493'}, ${props => props.bgEnd || '#ff69b4'});
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  cursor: pointer;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Vazirmatn', sans-serif;
  font-weight: 600;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }
`;

const PageDecoration = styled.div`
  position: absolute;
  top: ${props => props.top || '10px'};
  left: ${props => props.left || '10px'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  font-size: ${props => props.size || '1.5rem'};
  color: ${props => props.color || 'rgba(216, 64, 121, 0.3)'};
  transform: rotate(${props => props.rotate || '0deg'});
  opacity: 0.7;
  z-index: 1;
`;

const Sparkle = styled(motion.div)`
  position: absolute;
  pointer-events: none;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: radial-gradient(circle, white 0%, rgba(255, 255, 255, 0) 70%);
  border-radius: 50%;
  z-index: 10;
`;

const RibbonDecoration = styled.div`
  position: absolute;
  top: ${props => props.top || '0'};
  right: ${props => props.right || '0'};
  width: 120px;
  height: 120px;
  overflow: hidden;
  z-index: 10;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 150%;
    height: 40px;
    background: ${props => props.color || '#ff1493'};
    transform: rotate(45deg) translateX(60px) translateY(-20px);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  }
  
  &:after {
    content: '💖';
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 1.2rem;
    transform: rotate(45deg);
    color: white;
  }
`;

const Corner = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background: transparent;
  z-index: 5;
  
  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(216, 64, 121, 0.2);
    border-radius: ${props => props.position === 'topLeft' 
      ? '0 0 100% 0' 
      : props.position === 'topRight' 
        ? '0 0 0 100%' 
        : props.position === 'bottomLeft' 
          ? '0 100% 0 0' 
          : '100% 0 0 0'};
  }
`;

const PhotoAlbum = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [sparkles, setSparkles] = useState([]);
  const bookRef = useRef(null);
  const rotateY = useMotionValue(0);
  const rotateX = useMotionValue(10);
  const pageRefs = useRef([]);
  
  const totalPages = 6; // Total number of pages in the album

  // Function to turn to the next page
  const nextPage = () => {
    if (currentPage < totalPages && !isFlipping) {
      setIsFlipping(true);
      
      // Create page turning animation
      const currentPageEl = pageRefs.current[currentPage];
      if (currentPageEl) {
        currentPageEl.style.transform = 'rotateY(-180deg)';
        createSparkleEffect();
      }
      
      setTimeout(() => {
        setCurrentPage(prevPage => prevPage + 1);
        setIsFlipping(false);
      }, 600);
    }
  };

  // Function to turn to the previous page
  const prevPage = () => {
    if (currentPage > 0 && !isFlipping) {
      setIsFlipping(true);
      
      // Create reverse page turning animation
      const prevPageEl = pageRefs.current[currentPage - 1];
      if (prevPageEl) {
        prevPageEl.style.transform = 'rotateY(0deg)';
        createSparkleEffect();
      }
      
      setTimeout(() => {
        setCurrentPage(prevPage => prevPage - 1);
        setIsFlipping(false);
      }, 600);
    }
  };

  // Handle 3D effect on mouse move
  const handleMouseMove = (e) => {
    if (!bookRef.current) return;
    
    const rect = bookRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const rotateXValue = ((e.clientY - centerY) / 30) * -1 + 10;
    const rotateYValue = (e.clientX - centerX) / 30;
    
    rotateX.set(Math.min(Math.max(rotateXValue, 0), 20));
    rotateY.set(Math.min(Math.max(rotateYValue, -15), 15));
  };

  // Reset rotation on mouse leave
  const handleMouseLeave = () => {
    rotateX.set(10);
    rotateY.set(0);
  };

  // Create sparkle effect during page turning
  const createSparkleEffect = () => {
    const newSparkles = [];
    for (let i = 0; i < 10; i++) {
      newSparkles.push({
        id: Date.now() + i,
        x: Math.random() * 600,
        y: Math.random() * 400,
        size: Math.random() * 10 + 5,
        duration: Math.random() * 0.8 + 0.4,
      });
    }
    
    setSparkles(newSparkles);
    
    setTimeout(() => {
      setSparkles([]);
    }, 1000);
  };

  // Register page refs on component mount
  useEffect(() => {
    pageRefs.current = pageRefs.current.slice(0, totalPages);
  }, []);

  // Create empty photo frames for each page
  const createEmptyFrames = (pageNumber, isReversed = false) => {
    const frames = [];
    
    // Different layout for each page
    switch(pageNumber) {
      case 0:
        frames.push(
          <EmptyPhotoFrame key="frame1" rotate="-3deg" whileHover={{ scale: 1.05, rotate: '-1deg' }}>
            <FrameContent>
              <FrameIcon>🖼️</FrameIcon>
              <FrameText>نام عکس</FrameText>
            </FrameContent>
          </EmptyPhotoFrame>
        );
        break;
      case 1:
        frames.push(
          <EmptyPhotoFrame key="frame2" rotate="2deg" whileHover={{ scale: 1.05, rotate: '4deg' }}>
            <FrameContent>
              <FrameIcon>🖼️</FrameIcon>
              <FrameText>نام عکس</FrameText>
            </FrameContent>
          </EmptyPhotoFrame>
        );
        break;
      case 2:
        frames.push(
          <EmptyPhotoFrame key="frame3" rotate="-4deg" whileHover={{ scale: 1.05, rotate: '-2deg' }}>
            <FrameContent>
              <FrameIcon>🖼️</FrameIcon>
              <FrameText>نام عکس</FrameText>
            </FrameContent>
          </EmptyPhotoFrame>
        );
        break;
      case 3:
        frames.push(
          <EmptyPhotoFrame key="frame4" rotate="3deg" whileHover={{ scale: 1.05, rotate: '5deg' }}>
            <FrameContent>
              <FrameIcon>🖼️</FrameIcon>
              <FrameText>نام عکس</FrameText>
            </FrameContent>
          </EmptyPhotoFrame>
        );
        break;
      case 4:
        frames.push(
          <EmptyPhotoFrame key="frame5" rotate="-2deg" whileHover={{ scale: 1.05, rotate: '0deg' }}>
            <FrameContent>
              <FrameIcon>🖼️</FrameIcon>
              <FrameText>نام عکس</FrameText>
            </FrameContent>
          </EmptyPhotoFrame>
        );
        break;
      case 5:
        frames.push(
          <EmptyPhotoFrame key="frame6" rotate="4deg" whileHover={{ scale: 1.05, rotate: '6deg' }}>
            <FrameContent>
              <FrameIcon>🖼️</FrameIcon>
              <FrameText>نام عکس</FrameText>
            </FrameContent>
          </EmptyPhotoFrame>
        );
        break;
      default:
        frames.push(
          <EmptyPhotoFrame key={`frame-default-${pageNumber}`} rotate="0deg" whileHover={{ scale: 1.05 }}>
            <FrameContent>
              <FrameIcon>🖼️</FrameIcon>
              <FrameText>نام عکس</FrameText>
            </FrameContent>
          </EmptyPhotoFrame>
        );
    }
    
    return (
      <TextContainer isReversed={isReversed}>
        <PageContent>{frames}</PageContent>
      </TextContainer>
    );
  };

  // Get captions for each page
  const getPageCaption = (pageNumber, isReversed = false) => {
    const captions = [
      "خاطرات یک روز خوب که با هم داشتیم",
      "لحظه ای فراموش نشدنی که کنار هم بودیم",
      "عشق ما روز به روز قوی تر می شود",
      "همیشه در قلب من جای داری",
      "دنیایم با تو معنی پیدا می کند",
      "تا ابد عاشقت خواهم ماند"
    ];
    
    return (
      <TextContainer isReversed={isReversed}>
        <PageCaption>{captions[pageNumber] || "صفحه جدید برای خاطرات جدید"}</PageCaption>
      </TextContainer>
    );
  };

  // Get decorations for each page
  const getPageDecorations = (pageNumber) => {
    const decorations = [];
    
    // Add different decorations based on page number
    switch(pageNumber) {
      case 0:
        decorations.push(
          <PageDecoration key="dec1" top="20px" left="20px" size="1.8rem" color="rgba(216, 64, 121, 0.4)">❤️</PageDecoration>,
          <PageDecoration key="dec2" bottom="20px" right="20px" size="1.6rem" color="rgba(216, 64, 121, 0.4)">💕</PageDecoration>
        );
        break;
      case 1:
        decorations.push(
          <PageDecoration key="dec3" top="30px" right="30px" size="1.7rem" rotate="15deg" color="rgba(216, 64, 121, 0.4)">🌸</PageDecoration>,
          <PageDecoration key="dec4" bottom="30px" left="25px" size="1.5rem" rotate="-10deg" color="rgba(216, 64, 121, 0.4)">✨</PageDecoration>
        );
        break;
      case 2:
        decorations.push(
          <PageDecoration key="dec5" top="25px" left="40px" size="1.6rem" color="rgba(216, 64, 121, 0.4)">💖</PageDecoration>,
          <PageDecoration key="dec6" bottom="40px" right="35px" size="1.4rem" rotate="-5deg" color="rgba(216, 64, 121, 0.4)">🌹</PageDecoration>
        );
        break;
      case 3:
        decorations.push(
          <PageDecoration key="dec7" top="35px" right="25px" size="1.7rem" rotate="10deg" color="rgba(216, 64, 121, 0.4)">💘</PageDecoration>,
          <PageDecoration key="dec8" bottom="20px" left="40px" size="1.5rem" rotate="-8deg" color="rgba(216, 64, 121, 0.4)">🎀</PageDecoration>
        );
        break;
      case 4:
        decorations.push(
          <PageDecoration key="dec9" top="20px" left="30px" size="1.8rem" color="rgba(216, 64, 121, 0.4)">💓</PageDecoration>,
          <PageDecoration key="dec10" bottom="35px" right="20px" size="1.6rem" rotate="12deg" color="rgba(216, 64, 121, 0.4)">🌺</PageDecoration>
        );
        break;
      case 5:
        decorations.push(
          <PageDecoration key="dec11" top="30px" right="40px" size="1.7rem" rotate="-10deg" color="rgba(216, 64, 121, 0.4)">💝</PageDecoration>,
          <PageDecoration key="dec12" bottom="25px" left="20px" size="1.5rem" rotate="5deg" color="rgba(216, 64, 121, 0.4)">✨</PageDecoration>
        );
        break;
      default:
        decorations.push(
          <PageDecoration key={`dec-default-1-${pageNumber}`} top="20px" left="20px" size="1.5rem" color="rgba(216, 64, 121, 0.4)">❤️</PageDecoration>,
          <PageDecoration key={`dec-default-2-${pageNumber}`} bottom="20px" right="20px" size="1.5rem" color="rgba(216, 64, 121, 0.4)">💕</PageDecoration>
        );
    }
    
    return decorations;
  };

  // Create page content for the book
  const createPages = () => {
    const pages = [];
    
    for (let i = 0; i < totalPages; i++) {
      // Front of the page
      pages.push(
        <Page 
          key={`page-front-${i}`}
          style={{ zIndex: totalPages - i }}
          ref={el => pageRefs.current[i] = el}
        >
          {getPageDecorations(i)}
          {createEmptyFrames(i)}
          {getPageCaption(i)}
          <PageNumber right="20px">{i * 2 + 1}</PageNumber>
          <Corner position="topRight" style={{ top: 0, right: 0 }} />
        </Page>
      );
      
      // Back of the page
      pages.push(
        <PageBack 
          key={`page-back-${i}`}
          style={{ zIndex: totalPages - i - 0.5 }}
        >
          {getPageDecorations(i)}
          {createEmptyFrames(i, true)}
          {getPageCaption(i, true)}
          <PageNumber left="20px">{i * 2 + 2}</PageNumber>
          <Corner position="topLeft" style={{ top: 0, left: 0 }} />
        </PageBack>
      );
    }
    
    return pages;
  };

  return (
    <AlbumContainer>
      <AlbumTitle 
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        آلبوم عکس
      </AlbumTitle>
      
      <AlbumDescription
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
       صفحاتی از خاطرات زیبای ما که برای همیشه در قلبم ثبت شده است 
       <br />
       **بزودی با هم پرش میکنیم**
      </AlbumDescription>
      
      <BookContainer
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <Book
          ref={bookRef}
          style={{ 
            rotateY: rotateY, 
            rotateX: rotateX,
          }}
        >
          <FrontCover isOpen={currentPage > 0}>
            <RibbonDecoration top="0" right="0" color="#ff1493" />
            <FlowerDecoration top="20px" left="20px" color="#ff6b81" fontSize="2.5rem">🌹</FlowerDecoration>
            <FlowerDecoration bottom="20px" right="20px" color="#ff6b81" fontSize="2.5rem" rotate="15deg">🌸</FlowerDecoration>
            <CoverTitle>آلبوم عشق ما</CoverTitle>
            <CoverSubtitle>خاطرات دوست داشتن تو</CoverSubtitle>
          </FrontCover>
          
          {createPages()}
          
          <BackCover>
            <FlowerDecoration top="30px" left="30px" color="#ff6b81" fontSize="2.5rem" rotate="-10deg">💖</FlowerDecoration>
            <FlowerDecoration bottom="30px" right="30px" color="#ff6b81" fontSize="2.5rem" rotate="5deg">🌸</FlowerDecoration>
            <CoverTitle>پایان آلبوم</CoverTitle>
            <CoverSubtitle>اما نه پایان عشق ما...</CoverSubtitle>
          </BackCover>
        </Book>
        
        {/* Sparkle effects during page turning */}
        <AnimatePresence>
          {sparkles.map(sparkle => (
            <Sparkle
              key={sparkle.id}
              size={sparkle.size}
              style={{
                left: sparkle.x,
                top: sparkle.y,
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: sparkle.duration }}
            />
          ))}
        </AnimatePresence>
      </BookContainer>
      
      <NavigationButtons>
        <NavButton 
          onClick={prevPage} 
          disabled={currentPage === 0}
          whileTap={{ scale: 0.95 }}
          whileHover={{ y: -5 }}
          bgStart="#ff69b4"
          bgEnd="#ff1493"
        >
          ← صفحه قبل
        </NavButton>
        
        <NavButton 
          onClick={nextPage} 
          disabled={currentPage >= totalPages}
          whileTap={{ scale: 0.95 }}
          whileHover={{ y: -5 }}
          bgStart="#ff1493"
          bgEnd="#ff69b4"
        >
          صفحه بعد →
        </NavButton>
      </NavigationButtons>
    </AlbumContainer>
  );
};

export default PhotoAlbum; 